/**
 * @typedef {Object} Client
 * @property {string} id - Client unique identifier
 * @property {string} company - Company name
 * @property {string} name - Contact person name
 * @property {string} email - Contact email address
 * @property {'ecommerce'|'basic'|'premium'|'local'} pack - Service package type
 * @property {'active'|'inactive'|'pending'} status - Client status
 * @property {string} joinDate - Date client joined (ISO string)
 * @property {string} lastContact - Last contact date (ISO string)
 */

/**
 * @typedef {Object} Project
 * @property {string} id - Project unique identifier
 * @property {string} name - Project name
 * @property {string} description - Project description
 * @property {number} progress - Progress percentage (0-100)
 * @property {number} budget - Project budget
 * @property {number} tasksCount - Number of tasks
 * @property {string} endDate - Project end date (ISO string)
 * @property {'planning'|'in-progress'|'completed'} status - Project status
 * @property {string} clientId - Associated client ID
 * @property {string} clientName - Associated client name
 */

/**
 * @typedef {Object} Conversation
 * @property {string} id - Conversation unique identifier
 * @property {string} clientName - Client name
 * @property {string} subject - Conversation subject
 * @property {string} lastMessage - Last message content
 * @property {string} lastMessageDate - Last message date (ISO string)
 * @property {'active'|'pending'|'closed'} status - Conversation status
 * @property {number} unreadCount - Number of unread messages
 * @property {'low'|'medium'|'high'} priority - Conversation priority
 */

/**
 * @typedef {Object} DashboardMetrics
 * @property {number} totalClients - Total number of clients
 * @property {number} activeProjects - Number of active projects
 * @property {number} activeConversations - Number of active conversations
 * @property {number} pendingOnboardings - Number of pending onboardings
 */

/** @type {Client[]} */
export const mockClients = [
  {
    id: '1',
    company: 'TechCorp España',
    name: 'Laura Sánchez',
    email: 'laura@techcorp.es',
    pack: 'premium',
    status: 'active',
    joinDate: '2024-01-15',
    lastContact: '2024-08-05'
  },
  {
    id: '2',
    company: 'Boutique Mediterránea',
    name: 'Miguel Rodríguez',
    email: 'miguel@boutique.es',
    pack: 'ecommerce',
    status: 'active',
    joinDate: '2024-02-20',
    lastContact: '2024-08-06'
  },
  {
    id: '3',
    company: 'Consultoría Jurídica',
    name: 'Carmen Jiménez',
    email: 'carmen@juridica.es',
    pack: 'basic',
    status: 'active',
    joinDate: '2024-03-10',
    lastContact: '2024-08-04'
  },
  {
    id: '4',
    company: 'Restaurante El Olivo',
    name: 'Antonio Morales',
    email: 'antonio@elolivo.es',
    pack: 'local',
    status: 'pending',
    joinDate: '2024-07-28',
    lastContact: '2024-08-07'
  },
  {
    id: '5',
    company: 'Gimnasio FitLife',
    name: 'Elena Vázquez',
    email: 'elena@fitlife.es',
    pack: 'premium',
    status: 'active',
    joinDate: '2024-04-05',
    lastContact: '2024-08-03'
  }
];

/** @type {Project[]} */
export const mockProjects = [
  {
    id: '1',
    name: 'Rediseño Web TechCorp',
    description: 'Renovación completa del sitio web corporativo con nuevo sistema de gestión',
    progress: 75,
    budget: 15000,
    tasksCount: 24,
    endDate: '2024-09-15',
    status: 'in-progress',
    clientId: '1',
    clientName: 'TechCorp España'
  },
  {
    id: '2',
    name: 'E-commerce Boutique',
    description: 'Desarrollo de tienda online con integración de pasarela de pago',
    progress: 90,
    budget: 8500,
    tasksCount: 18,
    endDate: '2024-08-30',
    status: 'in-progress',
    clientId: '2',
    clientName: 'Boutique Mediterránea'
  },
  {
    id: '3',
    name: 'SEO Consultoría',
    description: 'Optimización SEO y estrategia de contenidos para mejorar posicionamiento',
    progress: 45,
    budget: 3500,
    tasksCount: 12,
    endDate: '2024-10-20',
    status: 'in-progress',
    clientId: '3',
    clientName: 'Consultoría Jurídica'
  },
  {
    id: '4',
    name: 'Marketing Local Restaurante',
    description: 'Campaña de marketing local y gestión de redes sociales',
    progress: 25,
    budget: 2500,
    tasksCount: 8,
    endDate: '2024-11-10',
    status: 'planning',
    clientId: '4',
    clientName: 'Restaurante El Olivo'
  },
  {
    id: '5',
    name: 'App Móvil FitLife',
    description: 'Desarrollo de aplicación móvil para gestión de clientes y clases',
    progress: 100,
    budget: 12000,
    tasksCount: 32,
    endDate: '2024-07-31',
    status: 'completed',
    clientId: '5',
    clientName: 'Gimnasio FitLife'
  }
];

/** @type {Conversation[]} */
export const mockConversations = [
  {
    id: '1',
    clientName: 'Laura Sánchez',
    subject: 'Revisión del diseño del header',
    lastMessage: 'Perfecto, me gusta mucho la nueva propuesta. ¿Cuándo podemos implementarla?',
    lastMessageDate: '2024-08-07T10:30:00',
    status: 'active',
    unreadCount: 2,
    priority: 'medium'
  },
  {
    id: '2',
    clientName: 'Miguel Rodríguez',
    subject: 'Problema con pasarela de pago',
    lastMessage: 'Sigue apareciendo error al procesar los pagos con tarjeta',
    lastMessageDate: '2024-08-07T09:15:00',
    status: 'active',
    unreadCount: 1,
    priority: 'high'
  },
  {
    id: '3',
    clientName: 'Carmen Jiménez',
    subject: 'Consulta sobre analytics',
    lastMessage: 'Gracias por el informe mensual, todo muy claro',
    lastMessageDate: '2024-08-06T16:45:00',
    status: 'closed',
    unreadCount: 0,
    priority: 'low'
  },
  {
    id: '4',
    clientName: 'Antonio Morales',
    subject: 'Configuración inicial',
    lastMessage: 'Necesito ayuda para completar la configuración del perfil',
    lastMessageDate: '2024-08-07T11:20:00',
    status: 'pending',
    unreadCount: 3,
    priority: 'medium'
  },
  {
    id: '5',
    clientName: 'Elena Vázquez',
    subject: 'Nuevas funcionalidades',
    lastMessage: 'Me encanta la nueva app! ¿Podrían añadir notificaciones push?',
    lastMessageDate: '2024-08-05T14:30:00',
    status: 'active',
    unreadCount: 0,
    priority: 'low'
  }
];

/** @type {DashboardMetrics} */
export const mockDashboardMetrics = {
  totalClients: mockClients.length,
  activeProjects: mockProjects.filter(p => p.status === 'in-progress').length,
  activeConversations: mockConversations.filter(c => c.status === 'active').length,
  pendingOnboardings: 3
};

export const statusLabels = {
  active: 'Activo',
  inactive: 'Inactivo',
  pending: 'Pendiente',
  planning: 'Planificación',
  'in-progress': 'En Progreso',
  completed: 'Completado',
  closed: 'Cerrada',
  low: 'Baja',
  medium: 'Media',
  high: 'Alta'
};
